import { LANGS, type Lang, type Locale } from "../config/langs";
import { LEVELS } from "../config/defaults";
import type { CardList } from "../config/lists";
import type { Card, CefrLevel } from "../types";
import { normalizeHeader } from "./text";

// RFC 4180-ish CSV parser. Handles quoted fields containing commas,
// newlines, or doubled quotes. Rejects fully empty rows.
export function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let i = 0;
  let inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cur += c;
      i++;
    } else {
      if (c === '"') {
        inQuotes = true;
        i++;
      } else if (c === ",") {
        row.push(cur);
        cur = "";
        i++;
      } else if (c === "\n" || c === "\r") {
        row.push(cur);
        rows.push(row);
        cur = "";
        row = [];
        if (c === "\r" && text[i + 1] === "\n") i += 2;
        else i++;
      } else {
        cur += c;
        i++;
      }
    }
  }
  if (cur.length || row.length) {
    row.push(cur);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c && c.trim().length));
}

// Errors thrown by parseCards. Keep them typed so the caller can i18n
// them without string-matching. The `key` matches a dict entry.
export class CardParseError extends Error {
  constructor(public readonly key: "parse_error_min" | "parse_error_empty" | "parse_error_no_cards") {
    super(key);
  }
}

/**
 * Parse a deck CSV into `Card[]`.
 *
 * `list` / `locale` are optional to preserve the legacy bilingual
 * signature used by tests and in-memory card construction. When they're
 * omitted, the function behaves exactly as before (bilingual, reading
 * Lang-coded columns fr-FR/bg-BG/en-GB). When they're passed and
 * `list.shape === "mono"`, columns are resolved per face as
 * `{face.key}_{locale}` (full "fr-FR" matched first, then short "fr",
 * then the bare key as a one-locale fallback).
 */
export function parseCards(
  csvText: string,
  list?: CardList,
  locale?: Locale,
): Card[] {
  const rows = parseCSV(csvText);
  if (rows.length < 2) throw new CardParseError("parse_error_empty");
  const headers = rows[0]!.map((h) => normalizeHeader(h));
  const findCol = (...names: string[]): number => {
    for (const n of names) {
      const idx = headers.indexOf(n.toLowerCase());
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const tagIdx = findCol("tags", "tag", "categorie", "catégorie", "category");
  const levelIdx = findCol("level", "niveau", "cefr");
  const showIdx = findCol("show");

  // Extras: any column whose header starts with `extras.` becomes a key
  // in `card.extras`. Example: `extras.phonetic`, `extras.audio_url`.
  // Empty cells are skipped so cards only carry the extras that apply.
  const extrasCols: { key: string; idx: number }[] = [];
  for (let h = 0; h < headers.length; h++) {
    const name = headers[h]!;
    if (name.startsWith("extras.") && name.length > "extras.".length) {
      extrasCols.push({ key: name.slice("extras.".length), idx: h });
    }
  }

  const isMono = list?.shape === "mono";

  // Resolve per-face column indices. Bilingual path stays close to the
  // legacy behaviour (language-coded columns + old français/bulgare
  // fallbacks); mono path uses `{face.key}_{locale}` with graceful
  // shortening and a bare-key fallback.
  type FaceCol = { key: string; idx: number; isIdCandidate: boolean };
  const faceCols: FaceCol[] = [];
  if (isMono) {
    const loc: Locale = locale ?? "fr";
    for (const f of list!.faces) {
      const idx = findCol(`${f.key}_${loc}`, `${f.key}_${loc.toLowerCase()}`, f.key);
      faceCols.push({ key: f.key, idx, isIdCandidate: true });
    }
    // Need every face to resolve for a mono deck — a missing face
    // column makes the whole deck unusable.
    if (faceCols.some((f) => f.idx === -1)) {
      throw new CardParseError("parse_error_min");
    }
  } else {
    // Bilingual: language-coded columns. Prefer face config when given
    // (so a future 3+ lang catalog can opt into a smaller pair), else
    // fall back to LANGS with legacy name aliases.
    const wantedLangs: Lang[] = list
      ? (list.faces.map((f) => f.lang).filter(Boolean) as Lang[])
      : [...LANGS];
    const langs: Lang[] = wantedLangs.length >= 2 ? wantedLangs : [...LANGS];
    for (const lang of langs) {
      const idx =
        lang === "fr-FR"
          ? findCol("fr-fr", "francais", "français", "fr", "french")
          : lang === "bg-BG"
            ? findCol("bg-bg", "bulgare", "bulgarian", "bg")
            : findCol("en-gb", "en-us", "english", "en", "anglais");
      if (idx !== -1) faceCols.push({ key: lang, idx, isIdCandidate: true });
    }
    if (faceCols.length < 2) {
      throw new CardParseError("parse_error_min");
    }
  }

  // For id stability across bilingual list schema tweaks, prefer
  // fr-FR then bg-BG then en-GB when present — same priority the old
  // code used. For mono, the first face is the id source (matches the
  // deck's natural prompt direction).
  const bilingualIdPriority: readonly Lang[] = ["fr-FR", "bg-BG", "en-GB"];

  const cards: Card[] = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i]!;
    const faces: Partial<Record<string, string>> = {};
    for (const f of faceCols) {
      const v = (r[f.idx] || "").trim();
      if (v) faces[f.key] = v;
    }
    // Need at least 2 non-empty face fields to be useful.
    if (Object.keys(faces).length < 2) continue;
    // Skip cards marked as hidden (show=N).
    if (showIdx !== -1) {
      const showVal = (r[showIdx] || "").trim().toUpperCase();
      if (showVal === "N") continue;
    }
    // Stable id. Bilingual preserves the historical priority; mono
    // uses the first face key.
    let idKey: string;
    if (isMono) {
      idKey = faceCols[0]!.key;
    } else {
      const fromPrio = bilingualIdPriority.find((l) => faces[l]);
      idKey = fromPrio ?? faceCols[0]!.key;
    }
    const id = idKey + "::" + faces[idKey];

    const tagsRaw = tagIdx !== -1 ? (r[tagIdx] || "").trim() : "";
    const rawLevel = (levelIdx !== -1 ? (r[levelIdx] || "").trim().toUpperCase() : "") as CefrLevel;
    const level: CefrLevel = (LEVELS as readonly string[]).indexOf(rawLevel) >= 0 ? rawLevel : "B1";
    let extras: Record<string, unknown> | undefined;
    for (const { key, idx } of extrasCols) {
      const v = (r[idx] || "").trim();
      if (!v) continue;
      if (!extras) extras = {};
      extras[key] = v;
    }
    cards.push({
      id,
      faces,
      level,
      tags: tagsRaw
        ? tagsRaw
            .split(/[,;]/)
            .map((t) => t.trim())
            .filter(Boolean)
        : [],
      ...(extras ? { extras } : {}),
    });
  }
  if (!cards.length) throw new CardParseError("parse_error_no_cards");
  return cards;
}
